/**
 * 秋田県の背景ツアー「秋田めぐり」。10か所、それぞれに3つの豆知識。
 *
 * 書いてはいけないこと。
 * - 田沢湖の「たつこ像」は近代の彫刻なので画面に入れない。湖面と山だけで構図が成り立つ。
 * - 寒風山の回転展望台は現役の建築物なので描かない。山の稜線だけにする。
 * - 入道崎のモニュメントは作者が確認できないので入れていない（入道崎自体を採っていない）。
 * - 抱返り渓谷の散策路にある「飯村少年碑」は性格が確認できないので画面に入れない。
 * - 岳岱の「400年ブナ」は2022年の冬に倒壊している。立っている姿を描かない。
 * - 岳岱は白神山地の核心地域そのものではなく、周辺区域である。そう書く。
 * - 鳥海山は信仰の山で、史跡の指定範囲に神社が含まれる。山体の遠景だけを描く。
 * - 大湯環状列石と川原毛地獄は、公的な出典が集団墓や霊地と記しているので入れていない。
 * - 白神山地のブナ林そのものは青森側で採っているので、秋田側は岳岱の自然観察教育林にした。
 */

import type { StageTour } from '../types';

export const akitaSceneryTour: StageTour = {
  scenes: [
    {
      id: 'kakunodate',
      name: '角館 武家屋敷',
      area: '仙北市',
      image: '/assets/scenery/akita/scenes/01_kakunodate.webp',
      hotspots: [
        {
          id: 'kakunodate-district',
          x: 0.25,
          y: 0.35,
          title: '昭和51年に武家町として選定',
          body: '重要伝統的建造物群保存地区「仙北市角館」は種別が武家町、面積6.9ヘクタール。昭和51年9月4日に選定された。',
          source: 'https://www.bunka.go.jp/seisaku/bunkazai/shokai/hozonchiku/pdf/r1392257_007.pdf',
          mapQuery: '角館 武家屋敷',
        },
        {
          id: 'kakunodate-feature',
          x: 0.55,
          y: 0.6,
          title: '塀と木立が特性を残す',
          body: '文化庁は「広い通り沿いに塀が連続し、シダレザクラやモミの大木が深い木立を形成し、江戸時代末期時の屋敷割や、主屋・門・蔵の屋敷構えとともに武家町の特性をよく残している」と記す。',
          source: 'https://www.bunka.go.jp/seisaku/bunkazai/shokai/hozonchiku/pdf/r1392257_007.pdf',
          mapQuery: '角館町 武家屋敷通り',
        },
        {
          id: 'kakunodate-sasarako',
          x: 0.8,
          y: 0.3,
          title: 'のぞき窓のついた黒い塀',
          body: '青柳家には道路に面して、のぞき窓のついた黒塗りのささら子塀がめぐる。石黒家にものぞき窓を付けた黒板塀がめぐり、岩橋家には推定樹齢300年あまりのカシワの木が枝垂桜とともに残る。',
          source: 'https://www.city.semboku.akita.jp/sightseeing/spot/07_buke.html',
          mapQuery: '角館 青柳家',
        },
      ],
    },
    {
      id: 'kisakata',
      name: '象潟',
      area: 'にかほ市',
      image: '/assets/scenery/akita/scenes/02_kisakata.webp',
      hotspots: [
        {
          id: 'kisakata-tennen',
          x: 0.25,
          y: 0.55,
          title: '昭和9年に天然記念物',
          body: '昭和9年（1934年）に国の天然記念物に指定された。指定の理由は「火山及地震ニヨル土地ノ隆起及陷落ヲ示セル自然記録トシテ興味アリ且重要ナルモノナリ」。',
          source: 'https://online.bunka.go.jp/heritages/detail/215829',
          mapQuery: '象潟',
        },
        {
          id: 'kisakata-islands',
          x: 0.55,
          y: 0.72,
          title: '島の数は105',
          body: '天然記念物「象潟」の島の数は全部で105島。地質的には約2500年前の鳥海山の山体崩壊によってできた流れ山である。',
          source:
            'https://www.city.nikaho.akita.jp/soshikikarasagasu/bunkazaihogoka/gyomuannai/2/1/1511.html',
          mapQuery: '象潟 九十九島',
        },
        {
          id: 'kisakata-uplift',
          x: 0.78,
          y: 0.35,
          title: '地震で海が干上がった',
          body: '1804年に象潟付近で地震が起き、2メートルほど隆起した。このため古い象潟湖は干上がり、流れ山の周りに水田が広がる今の光景になった。1689年にここを訪れた芭蕉が見たのは、まだ湖だったころの景観である。',
          source: 'https://online.bunka.go.jp/heritages/detail/215829',
          mapQuery: '象潟 にかほ市',
        },
      ],
    },
    {
      id: 'takedai',
      name: '岳岱のブナ林',
      area: '藤里町',
      image: '/assets/scenery/akita/scenes/03_takedai.webp',
      hotspots: [
        {
          id: 'takedai-size',
          x: 0.25,
          y: 0.35,
          title: '標高620メートル、12ヘクタール',
          body: '岳岱自然観察教育林は標高が約620メートル、面積は約12ヘクタール。体力や時間に合わせて30分から半日ほど歩ける。',
          source: 'https://common3.pref.akita.lg.jp/shirakami/locations/1470',
          mapQuery: '岳岱自然観察教育林',
        },
        {
          id: 'takedai-buffer',
          x: 0.55,
          y: 0.6,
          title: '世界遺産地域の周辺区域',
          body: 'ブナを主体とする天然林で、世界遺産地域の周辺区域にありながら核心地域の様相を体験できる森のひとつとされている。',
          source: 'https://common3.pref.akita.lg.jp/shirakami/locations/1470',
          mapQuery: '藤里町 岳岱',
        },
        {
          id: 'takedai-garden',
          x: 0.78,
          y: 0.78,
          title: '自然の庭園のような森',
          body: '秋田県は「苔むした巨石が転がっている自然庭園のような風景が見られる」と紹介している。',
          source: 'https://common3.pref.akita.lg.jp/shirakami/locations/1470',
          mapQuery: '白神山地 岳岱',
        },
      ],
    },
    {
      id: 'mototaki',
      name: '元滝伏流水',
      area: 'にかほ市',
      image: '/assets/scenery/akita/scenes/04_mototaki.webp',
      hotspots: [
        {
          id: 'mototaki-meisui',
          x: 0.25,
          y: 0.3,
          title: '平成の名水百選',
          body: '環境省の「平成の名水百選」に選ばれている。所在は秋田県にかほ市。',
          source:
            'https://water-pub.env.go.jp/water-pub/mizu-site/newmeisui/data/index.asp?info=10',
          mapQuery: '元滝伏流水',
        },
        {
          id: 'mototaki-width',
          x: 0.55,
          y: 0.55,
          title: '幅約30メートルの岩肌から',
          body: '鳥海山に染み込んだ水が長い年月をかけて、幅およそ30メートルの岩肌一帯から湧き出している。',
          source:
            'https://water-pub.env.go.jp/water-pub/mizu-site/newmeisui/data/index.asp?info=10',
          mapQuery: 'にかほ市 元滝',
        },
        {
          id: 'mototaki-stable',
          x: 0.78,
          y: 0.8,
          title: '一年じゅう水量が変わらない',
          body: '年間を通じて水量が安定しており、地域の人の生活用水として今も使われている。この冷たい伏流水で育つ岩ガキは真夏が旬。',
          source:
            'https://water-pub.env.go.jp/water-pub/mizu-site/newmeisui/data/index.asp?info=10',
          mapQuery: '元滝伏流水 遊歩道',
        },
      ],
    },
    {
      id: 'kanpuzan',
      name: '寒風山',
      area: '男鹿市',
      image: '/assets/scenery/akita/scenes/05_kanpuzan.webp',
      hotspots: [
        {
          id: 'kanpuzan-height',
          x: 0.3,
          y: 0.35,
          title: '標高354.8メートル',
          body: '寒風山の標高は354.8メートル。山の上からは360度の眺めが広がり、男鹿市でも有数の人気観光スポットになっている。',
          source: 'https://common3.pref.akita.lg.jp/genkimura/archive/contents-408',
          mapQuery: '寒風山',
        },
        {
          id: 'kanpuzan-origin',
          x: 0.58,
          y: 0.6,
          title: '約2万年前の噴火',
          body: '約2万年前、氷河時代の終わりに噴火活動をくり返して形づくられた。',
          source: 'https://common3.pref.akita.lg.jp/genkimura/archive/contents-408',
          mapQuery: '男鹿市 寒風山',
        },
        {
          id: 'kanpuzan-geopark',
          x: 0.78,
          y: 0.8,
          title: '日本ジオパーク',
          body: '平成23年（2011年）に「男鹿半島・大潟」が日本ジオパークの認定を受けた。寒風山は地層観察に適したジオサイトのひとつ。',
          source: 'https://common3.pref.akita.lg.jp/genkimura/archive/contents-408',
          mapQuery: '男鹿半島 大潟ジオパーク',
        },
      ],
    },
    {
      id: 'ogata',
      name: '八郎潟干拓地',
      area: '大潟村',
      image: '/assets/scenery/akita/scenes/06_ogata.webp',
      hotspots: [
        {
          id: 'ogata-lake',
          x: 0.25,
          y: 0.35,
          title: 'もとは日本で2番目に大きい湖',
          body: '八郎潟は、琵琶湖に次いで日本で2番目に大きな湖だった。最深部でも水深4〜5メートルと非常に浅く、湖底は平坦だった。',
          source: 'https://www.maff.go.jp/tohoku/nouson/kokuei/hatirougata/rekisi.html',
          mapQuery: '八郎潟干拓地',
        },
        {
          id: 'ogata-project',
          x: 0.55,
          y: 0.6,
          title: '20年、852億円',
          body: '昭和32年に着工し、昭和41年に干陸、昭和52年3月に20年の歳月と約852億円を投じた大事業が完了した。八郎潟は17,239ヘクタールの大地へと生まれ変わった。',
          source: 'https://www.pref.akita.lg.jp/pages/archive/8842',
          mapQuery: '大潟村',
        },
        {
          id: 'ogata-village',
          x: 0.78,
          y: 0.82,
          title: '580戸の入植',
          body: '中央干拓地では昭和39年10月に新しい村「大潟村」が誕生した。昭和41年から入植者の募集が始まり、入植者は580戸、農地の配分面積は8,860ヘクタールで1戸あたり15ヘクタールだった。',
          source: 'https://www.maff.go.jp/tohoku/nouson/kokuei/hatirougata/rekisi.html',
          mapQuery: '大潟村 干拓博物館',
        },
      ],
    },
    {
      id: 'dakigaeri',
      name: '抱返り渓谷 回顧の滝',
      area: '仙北市',
      image: '/assets/scenery/akita/scenes/07_dakigaeri.webp',
      hotspots: [
        {
          id: 'dakigaeri-name',
          x: 0.25,
          y: 0.35,
          title: '抱きかかえて返すほど狭い道',
          body: '以前は人がすれ違うときにお互いに抱きかかえるように返さなければ通れないほど、狭く険しい山道だったことから「抱返り」と呼ばれるようになった。',
          source: 'https://www.city.semboku.akita.jp/sightseeing/spot/05_dakigaeri.html',
          mapQuery: '抱返り渓谷',
        },
        {
          id: 'dakigaeri-length',
          x: 0.55,
          y: 0.75,
          title: '全長10キロの渓谷',
          body: '田沢湖と角館のあいだを流れる玉川中流に続く、全長10キロメートルの渓谷。田沢湖卒田から角館町広久内までにあたる。',
          source: 'https://www.city.semboku.akita.jp/sightseeing/spot/05_dakigaeri.html',
          mapQuery: '抱返り渓谷 仙北市',
        },
        {
          id: 'dakigaeri-falls',
          x: 0.78,
          y: 0.45,
          title: '何度も振り返る滝',
          body: '回顧の滝は落差およそ30メートル。何度も振り返って見たくなるほど美しいことから「回顧（みかえり）の滝」と名づけられた。',
          source: 'https://www.city.semboku.akita.jp/sightseeing/spot/05_kaiko.html',
          mapQuery: '回顧の滝',
        },
      ],
    },
    {
      id: 'chokaisan',
      name: '鳥海山',
      area: '由利本荘市',
      image: '/assets/scenery/akita/scenes/08_chokaisan.webp',
      hotspots: [
        {
          id: 'chokaisan-height',
          x: 0.3,
          y: 0.3,
          title: '標高2236メートル',
          body: '標高2,236メートル（新山の測定点）。玄武岩から安山岩でできた成層火山で、別名を出羽富士、秋田富士という。気象庁の常時観測火山。',
          source: 'https://www.data.jma.go.jp/vois/data/sendai/209_Chokaisan/209_index.html',
          mapQuery: '鳥海山',
        },
        {
          id: 'chokaisan-history',
          x: 0.6,
          y: 0.6,
          title: '泥流を生じやすい山',
          body: '有史以降の活動は、1801年の噴火以外は火山灰の放出だった。泥流を生じやすい山とされている。',
          source: 'https://www.data.jma.go.jp/vois/data/sendai/209_Chokaisan/209_index.html',
          mapQuery: '鳥海山 由利本荘市',
        },
        {
          id: 'chokaisan-faith',
          x: 0.8,
          y: 0.82,
          title: '国の史跡でもある',
          body: '古代には国家鎮護の守護神として、中世には出羽国の中心的な信仰の山として崇敬され、近世にはさらに農業神として崇拝された。平成21年と平成28年に国指定文化財「鳥海山」へ追加指定されている。',
          source: 'https://www.city.yurihonjo.lg.jp/1001503/1002098/1002113/1002125/1003552.html',
          mapQuery: '鳥海山 由利本荘',
        },
      ],
    },
    {
      id: 'tazawako',
      name: '田沢湖',
      area: '仙北市',
      image: '/assets/scenery/akita/scenes/09_tazawako.webp',
      hotspots: [
        {
          id: 'tazawako-depth',
          x: 0.28,
          y: 0.4,
          title: '水深423.4メートル',
          body: '水深423.4メートルで、日本一の深さを誇る。',
          source: 'https://www.city.semboku.akita.jp/sightseeing/spot/04_tazawako.html',
          mapQuery: '田沢湖',
        },
        {
          id: 'tazawako-shape',
          x: 0.58,
          y: 0.62,
          title: '周囲約20キロのほぼ円形',
          body: '周囲はおよそ20キロメートルで、ほぼ円形をしている。',
          source: 'https://www.city.semboku.akita.jp/sightseeing/spot/04_tazawako.html',
          mapQuery: '田沢湖畔',
        },
        {
          id: 'tazawako-moon',
          x: 0.8,
          y: 0.3,
          title: '日本百名月',
          body: '2022年1月17日に、田沢湖は「日本百名月」に認定された。四季折々に表情が変わる、瑠璃色の湖面で知られる。',
          source: 'https://www.city.semboku.akita.jp/sightseeing/spot/04_tazawako.html',
          mapQuery: '田沢湖 仙北市',
        },
      ],
    },
    {
      id: 'oyasukyo',
      name: '小安峡 大噴湯',
      area: '湯沢市',
      image: '/assets/scenery/akita/scenes/10_oyasukyo.webp',
      hotspots: [
        {
          id: 'oyasukyo-geology',
          x: 0.28,
          y: 0.4,
          title: '地熱貯留層の亀裂が露出',
          body: '蒸気や熱水がたまっている地熱貯留層の亀裂が露出しているという、世界でも珍しい地形。いまも熱湯と蒸気が激しく噴き出している。',
          source: 'https://www.city-yuzawa.jp/site/yuzawatrip/736.html',
          mapQuery: '小安峡大噴湯',
        },
        {
          id: 'oyasukyo-record',
          x: 0.58,
          y: 0.65,
          title: '雷神のひびきのよう',
          body: '江戸時代の記録には、雷神のひびきのようなすごい音がして、水がはじけるように湯が噴き出していたと書かれている。',
          source: 'https://www.city-yuzawa.jp/site/yuzawatrip/736.html',
          mapQuery: '小安峡 湯沢市',
        },
        {
          id: 'oyasukyo-stairs',
          x: 0.8,
          y: 0.82,
          title: '約60メートル下る',
          body: '遊歩道は上流側の入口が302段、下流側が406段で、降下距離は約60メートル、所要は約30分。冬のあいだは通行止めになる。',
          source: 'https://www.city-yuzawa.jp/site/yuzawatrip/736.html',
          mapQuery: '小安峡',
        },
      ],
    },
  ],
};
